import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import Table from 'PDF/Elements/Table/Table';
import { useTemplateColors } from 'PDF/TemplateEngine/TemplateColorsContext';
import { evaluateFormula } from 'PDF/TemplateEngine/utils/evaluateFormula';
import { evaluateVisibleIf } from 'PDF/TemplateEngine/utils/evaluateVisibleIf';
import { formatMoney } from 'PDF/TemplateEngine/utils/formatMoney';
import { formatDate } from 'PDF/TemplateEngine/utils/formatDate';

// Formatea un valor según columnType declarado en la config del template.
function formatCell(value, columnType, moneda, dateFormat) {
  if (value == null) return '';
  if (columnType === 'money') return formatMoney(value, moneda);
  if (columnType === 'date') return formatDate(value, dateFormat);
  if (columnType === 'number') {
    const n = Number(value);
    return Number.isNaN(n) ? String(value) : n.toLocaleString('es-CL');
  }
  return String(value);
}

// Pre-procesa una fila agregando los valores computed y formateando las
// columnas con columnType declarado. Devuelve un objeto plano con strings.
function buildRowForTable(row, columns, computedColumns, data) {
  const out = {};
  for (const col of columns) {
    const raw = row[col.key];
    out[col.key] = formatCell(raw, col.columnType, data?.moneda, col.dateFormat);
  }
  for (const col of computedColumns) {
    let computed;
    try {
      computed = evaluateFormula(col.formula, { row, data });
    } catch {
      computed = null;
    }
    out[col.key] = formatCell(computed, col.columnType, data?.moneda, col.dateFormat);
  }
  return out;
}

function TableSection({ section, data }) {
  const { baseStyles, colors } = useTemplateColors();
  const cfg = section?.config || {};
  const columns = cfg.columns || [];
  const computedColumns = cfg.computedColumns || [];
  const dataKey = cfg.dataKey;
  const groupBy = cfg.groupBy;
  const footer = Array.isArray(cfg.footer) ? cfg.footer : [];

  let rows = Array.isArray(data?.[dataKey]) ? [...data[dataKey]] : [];

  // groupBy: sort por el campo para agrupar visualmente.
  if (groupBy) {
    rows.sort((a, b) => {
      const av = a?.[groupBy] ?? '';
      const bv = b?.[groupBy] ?? '';
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
  }

  const allColumns = [...columns, ...computedColumns];
  const tableColumns = allColumns.map((col) => ({
    key: col.key,
    title: col.title,
    width: col.width || `${Math.floor(100 / Math.max(allColumns.length, 1))}%`,
  }));
  const tableRows = rows.map((row) => buildRowForTable(row, columns, computedColumns, data));

  const footerStyles = StyleSheet.create({
    container: {
      marginTop: 0,
      border: `1px solid ${colors?.border || '#e8e8e8'}`,
      borderTop: 0,
    },
    row: {
      flexDirection: 'row',
      borderBottom: '1px solid #f0f0f0',
      backgroundColor: colors?.lightBg || '#e8f4fd',
    },
    rowEmphasis: {
      flexDirection: 'row',
      backgroundColor: colors?.primary || '#548235',
      borderTop: `2px solid ${colors?.darkText || '#2c3e50'}`,
    },
    label: {
      flex: 1,
      padding: '0.4cm 0.3cm',
      fontFamily: 'Helvetica-Bold',
      fontSize: '11px',
      color: colors?.darkText || '#2c3e50',
      textAlign: 'right',
      borderRight: '1px solid #d0d0d0',
    },
    labelEmphasis: {
      flex: 1,
      padding: '0.5cm 0.3cm',
      fontFamily: 'Helvetica-Bold',
      fontSize: '12px',
      color: colors?.white || '#ffffff',
      textAlign: 'right',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    value: {
      flex: 1,
      padding: '0.4cm 0.3cm',
      fontSize: '11px',
      color: colors?.darkText || '#2c3e50',
      textAlign: 'center',
      fontFamily: 'Helvetica-Bold',
    },
    valueEmphasis: {
      flex: 1,
      padding: '0.5cm 0.3cm',
      fontSize: '12px',
      color: colors?.white || '#ffffff',
      textAlign: 'center',
      fontFamily: 'Helvetica-Bold',
    },
  });

  const visibleFooter = footer.filter((f) => {
    if (!f?.visibleIf) return true;
    try {
      return evaluateVisibleIf(f.visibleIf, { data });
    } catch {
      return false;
    }
  });

  return (
    <View>
      {section?.title ? <Text style={baseStyles.sectionTitle}>{section.title}</Text> : null}
      <Table columns={tableColumns} data={tableRows} />
      {visibleFooter.length > 0 ? (
        <View style={footerStyles.container}>
          {visibleFooter.map((f, idx) => {
            let value = null;
            try {
              value = evaluateFormula(f.formula, { rows, data });
            } catch {
              value = null;
            }
            const formatted = formatCell(value, f.columnType, data?.moneda, f.dateFormat);
            const emphasis = Boolean(f.emphasis);
            return (
              <View
                key={`${f.label}-${idx}`}
                style={emphasis ? footerStyles.rowEmphasis : footerStyles.row}
                wrap={false}
              >
                <Text style={emphasis ? footerStyles.labelEmphasis : footerStyles.label}>
                  {f.label}
                </Text>
                <Text style={emphasis ? footerStyles.valueEmphasis : footerStyles.value}>
                  {formatted}
                </Text>
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

export default TableSection;
